import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import styled, { css } from 'styled-components';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { useAppSelector } from '@/redux/hooks';
import { getLiveCatalogCurrentItemId, getLiveItemDataLeadingBid } from '@/redux/modules/console';
import { getItemSummaryCurrency, getItemSummaryStartPrice } from '@/redux/modules/item/summary/itemSummary.selectors';
import noop from '@/utils/noop';

import ButtonGroup from './components/ButtonGroup/ButtonGroup';

type Props = {
    biddingLocked?: boolean;
    catalogId: number;
    className?: string;
    locked?: boolean;
    toggleBiddingLocked?: () => void;
};

const CTA = ({ biddingLocked = false, catalogId, className, locked = false, toggleBiddingLocked = noop }: Props) => {
    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));
    const leadingBid = useAppSelector((state) => getLiveItemDataLeadingBid(state, itemId));
    const currentBid = leadingBid || startPrice;

    return (
        <Container
            $locked={locked}
            className={className}
        >
            <PriceTextContainer>
                <PriceTextLabel>
                    <FormattedMessage id="current_bid" />:
                </PriceTextLabel>
                <PriceText>
                    <FormattedCurrency
                        key="current-bid-amount"
                        noFraction
                        sourceCurrency={currency}
                        value={currentBid || 0}
                    />
                </PriceText>
            </PriceTextContainer>
            <ButtonGroup
                biddingLocked={biddingLocked}
                catalogId={catalogId}
                mobile
                toggleBiddingLocked={toggleBiddingLocked}
            />
        </Container>
    );
};

export default CTA;

const PriceTextContainer = styled.div`
    margin: 0;
    font-size: 20px;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: row;
    padding: 11px 0 7px;
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    color: ${({ theme }) => theme.colors.black};
`;

const PriceTextLabel = styled.div`
    font-size: 16px;
    line-height: 26px;
    margin-right: 8px;
`;

const PriceText = styled.span`
    font-size: 16px;
    line-height: 26px;
`;

const Container = styled.div<{ $locked: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 0 15px;
    background-color: ${({ theme }) => theme.colors.white};
    border-top: 1px solid ${({ theme }) => theme.colors.grey400};
    transition: all 0.2s;
    width: 100%;
    flex-direction: column;
    align-items: inherit;
    padding: 0 15px;
    height: auto;
    text-align: center;

    ${({ $locked }) =>
        $locked &&
        css`
            border-top: none;
            position: fixed;
            top: 38px;
            left: 0;
            right: 0;
            z-index: 4;
            box-shadow: 0 0 5px ${({ theme }) => theme.colors.shadow200};
            border-bottom: 1px solid ${({ theme }) => theme.colors.grey300};

            button {
                margin-top: 0;
                height: 42px;
            }
        `}
`;
